import * as React from 'react';

import { useRequest } from 'ahooks';
import { Col, Form, Input, Row, Select } from 'antd';

import { AntdDateRangePicker, PrimitiveTreeSelect } from 'components';
import { getBrands, getCategories } from 'services';
import type { exportProducts } from 'services';
import type { ProductStatus } from 'types';
import {
  generateSelectOptions,
  generateTreeSelectData,
  inputRule,
  localizeProductStatus,
  normalizeToString,
} from 'utils';

const productStatuses = [
  'Submitted',
  'Draft',
  'Private',
] satisfies ProductStatus[];

type FieldType = Partial<
  Pick<
    Parameters<typeof exportProducts>[number],
    | 'searchKey'
    | 'statuses'
    | 'brandsIds'
    | 'categoriesIds'
    | 'callForPrice'
    | 'isIndex'
  > &
    Record<'duration', [unknown, unknown]>
>;

interface ProductsReportFormContentProps {
  setDurationRange: React.Dispatch<React.SetStateAction<[string?, string?]>>;
}

const ProductsReportFormContent = ({
  setDurationRange,
}: ProductsReportFormContentProps) => {
  const { data: categoriesData, loading: categoriesLoading } = useRequest(
    getCategories,
    {
      cacheKey: 'categories',
    },
  );
  const { data: brandsData, loading: brandsLoading } = useRequest(getBrands, {
    cacheKey: 'brands',
  });

  const { categories = [] } = categoriesData ?? {};
  const { brands = [] } = brandsData ?? {};

  const brandsSelectOptions = generateSelectOptions(
    brands,
    'id',
    'title',
    'id',
  );
  const categoriesTreeData = generateTreeSelectData(
    categories,
    'id',
    'title',
    'id',
  );

  return (
    <Row className='p-6'>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType>
          label='جستجو'
          name='searchKey'
          rules={[inputRule.string]}
        >
          <Input allowClear placeholder='عبارت جستجوی محصول‌ها را وارد کنید' />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType> label='وضعیت‌ها' name='statuses'>
          <Select
            allowClear
            mode='multiple'
            maxTagCount='responsive'
            placeholder='وضعیت‌های محصول‌ها را انتخاب کنید'
            options={productStatuses.map(type => {
              const { label } = localizeProductStatus(type);
              return {
                label,
                value: type,
              };
            })}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:pe-3'>
        <Form.Item<FieldType> label='استعلام بها' name='callForPrice'>
          <Select
            allowClear
            placeholder='استعلام بهای محصول‌ها را انتخاب کنید'
            options={[
              {
                label: 'دارای استعلام بها',
                value: 'true',
              },
              {
                label: 'فاقد استعلام بها',
                value: 'false',
              },
            ]}
          />
        </Form.Item>
      </Col>
      <Col span={24} lg={12} className='lg:ps-3'>
        <Form.Item<FieldType> label='نمایه‌گذاری' name='isIndex'>
          <Select
            allowClear
            placeholder='نمایه‌گذاری محصول‌ها را انتخاب کنید'
            options={[
              {
                label: 'دارای نمایه‌گذاری',
                value: 'true',
              },
              {
                label: 'فاقد نمایه‌گذاری',
                value: 'false',
              },
            ]}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='برندها' name='brandsIds'>
          <Select
            allowClear
            mode='multiple'
            placeholder='برندهای محصول‌ها را انتخاب کنید'
            loading={brandsLoading}
            options={brandsSelectOptions}
            filterOption={(inputValue, option) =>
              normalizeToString(option?.label)
                .toLowerCase()
                .includes(inputValue.toLowerCase())
            }
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='دسته‌بندی‌ها' name='categoriesIds'>
          <PrimitiveTreeSelect
            multiple
            treeCheckable
            treeCheckStrictly
            placeholder='دسته‌بندی‌های محصول‌ها را انتخاب کنید'
            loading={categoriesLoading}
            treeData={categoriesTreeData}
          />
        </Form.Item>
      </Col>
      <Col span={24}>
        <Form.Item<FieldType> label='بازه زمانی' name='duration'>
          <AntdDateRangePicker
            allowEmpty
            placeholder={[
              'تاریخ شروع ساخت محصول‌ها',
              'تاریخ پایان ساخت محصول‌ها',
            ]}
            onDateChange={([updatedLowerBound, updatedUpperBound]) => {
              setDurationRange([updatedLowerBound, updatedUpperBound]);
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  );
};

export { ProductsReportFormContent };
